import { css, SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import * as React from 'react'

import { breakpoints } from '../constants/breakPoints'
import AdPartners from './ads/AdPartners'
import BadgesBlock from './BadgesBlock'
import { DemoButton } from './Button'
import Image from './Image'
import { ListBlock } from './ListBlock'
import Partners from './Partners'

interface IntroProps {
  upperHeading?: string
  headlineText: string
  upperText?: React.ReactNode
  subheaderText?: string
  buttonText?: string
  mediaSrc?: string
  cssStyles?: SerializedStyles
  CTABlock?: React.ReactNode
  children?: React.ReactNode
  withBackgroundMesh?: boolean
  blueBG?: boolean
  blackBG?: boolean
  blueHeading?: boolean
  withoutCTA?: boolean
  largePadding?: boolean
  withoutMarginBottom?: boolean
  withModularityAnimation?: boolean
  isAdPage?: boolean
  withPartners?: boolean
  partnersText?: string
  withBadges?: boolean
  listItems?: string[]
  childrenIsFirst?: boolean
  childrenIsLast?: boolean
  withoutBorder?: boolean
  noMaxWidthForSubtitle?: boolean
}

export const IntroHeroBlock: React.FunctionComponent<IntroProps> = ({
  upperHeading,
  headlineText,
  upperText,
  subheaderText,
  buttonText,
  cssStyles,
  CTABlock,
  children,
  withBackgroundMesh = false,
  blueBG = false,
  blackBG = false,
  blueHeading = false,
  withoutCTA = false,
  largePadding = false,
  withoutMarginBottom = false,
  withModularityAnimation = false,
  isAdPage = false,
  withPartners = false,
  partnersText,
  withBadges = false,
  listItems,
  childrenIsFirst = false,
  childrenIsLast = false,
  withoutBorder = false,
  noMaxWidthForSubtitle = false,
}) => {
  return (
    <div
      css={[
        containerStyle(blueBG, blackBG, largePadding, withoutMarginBottom, withBackgroundMesh, withoutBorder),
        cssStyles,
      ]}
      id="intro-hero-block"
      className="full-screen-relative"
    >
      <div css={introBlockStyle}>
        {childrenIsFirst && children}
        {typeof upperText === 'string' ? <span className="subtitle white-text">{upperText}</span> : upperText}
        {upperHeading && <h1 className="subtitle gray-text">{upperHeading.toUpperCase()}</h1>}
        {upperHeading ? (
          <h2
            className={`heading1 small center ${
              (blueBG || blackBG) && !blueHeading ? 'white-text' : blueHeading ? 'blue-mesh-text' : 'black-text'
            }`}
            css={css`
              ${!subheaderText && 'margin-bottom: 0 !important;'}
            `}
          >
            {parse(headlineText)}
          </h2>
        ) : (
          <h1
            className={`heading1 center ${
              (blueBG || blackBG) && !blueHeading ? 'white-text' : blueHeading ? 'blue-mesh-text' : 'black-text'
            }`}
            css={css`
              ${!subheaderText && 'margin-bottom: 0 !important;'}
            `}
          >
            {parse(headlineText)}
          </h1>
        )}
        {subheaderText && (
          <div
            css={introSubtitleStyle(noMaxWidthForSubtitle)}
            className={`subtitle center ${blackBG && 'white-text'}`}
            dangerouslySetInnerHTML={{ __html: subheaderText }}
          />
        )}
        {!childrenIsLast && !childrenIsFirst && children}
        {!withoutCTA &&
          (CTABlock ? (
            CTABlock
          ) : (
            <div id="button-container" css={buttonContainerStyle(childrenIsLast, withModularityAnimation)}>
              <DemoButton buttonText={buttonText} />
            </div>
          ))}
      </div>

      {listItems && <ListBlock listItems={listItems} cssStyle={listBlockStyle(listItems.length)} />}

      {childrenIsLast && children}

      {withModularityAnimation && (
        <div css={modularityContainerStyle(withPartners)}>
          <Image
            src="product/modularity-hero.webp"
            alt="airfocus modularity"
            className="full-screen-relative image-with-animation"
            imgStyle={{ maxWidth: '1400px', height: 'auto', margin: 'auto' }}
            css={introImageStyle}
            loading="eager"
          />
        </div>
      )}

      {withPartners &&
        (isAdPage ? (
          <AdPartners partnersText={partnersText} isOnIntro />
        ) : (
          <Partners partnersText={partnersText} isOnIntro />
        ))}

      {withBadges && <BadgesBlock />}
    </div>
  )
}

const containerStyle = (
  blueBG: boolean,
  blackBG: boolean,
  largePadding: boolean,
  withoutMarginBottom: boolean,
  withBackgroundMesh: boolean,
  withoutBorder: boolean
) => css`
  position: relative;
  padding-top: 4rem;
  padding-bottom: ${largePadding ? '8rem' : '4rem'};
  margin-bottom: ${withoutMarginBottom ? '0rem' : '8rem'};
  color: #000;
  ${!withoutBorder && !blackBG && 'border-bottom: 1px solid #efeff0;'};

  ${blueBG &&
  `
    background-image: var(--blue-gradient);
    color: #fff !important;
  `}

  ${withBackgroundMesh &&
  `
    background-image: url('/images/hero${blackBG ? '-dark' : ''}.webp');
    background-repeat: no-repeat; 
    background-size: cover;
    background-position: center;
  `}

  @media screen and (max-width: ${breakpoints.large}px) {
    padding-top: 3rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    ${!withoutMarginBottom && 'margin-bottom: 4rem'};
  }
`

const introBlockStyle = css`
  z-index: 2;
  position: relative;
  top: 0;
  text-align: center;

  @media screen and (max-width: ${breakpoints.small}px) {
    padding-top: 0;
  }

  .heading2 {
    text-align: left;

    @media screen and (max-width: ${breakpoints.medium}px) {
      max-width: initial;
      width: 100%;
    }
  }
`

const introSubtitleStyle = (noMaxWithForSubtitle: boolean) => css`
  ${!noMaxWithForSubtitle && 'max-width: 39.6rem'};
  margin-left: auto !important;
  margin-right: auto !important;
`

const buttonContainerStyle = (childrenIsLast: boolean, withModularityAnimation: boolean) => css`
  margin-top: 1rem;
  ${(childrenIsLast || withModularityAnimation) && 'margin-bottom: 3rem;'};
`

const listBlockStyle = (listItemsLength: number) => css`
  li {
    grid-template-columns: min-content max-content !important;
  }

  @media screen and (min-width: ${(listItemsLength % 2 ? breakpoints.medium : breakpoints.small) + 1}px) {
    ${listItemsLength > 3 &&
    !(listItemsLength % 2) &&
    `
      ul {
        display: grid !important;
        grid-template-columns: repeat(2, max-content);
        margin: auto;

        li {
          margin: 0.75rem 0;
        }
      }
    `}
  }

  ul {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 2rem;
    flex-wrap: wrap;
    margin-bottom: 0;

    li {
      margin-bottom: 0;
    }

    @media screen and (max-width: ${listItemsLength > 2 && !(listItemsLength % 2) ? '800' : breakpoints.small}px) {
      flex-direction: column;

      li {
        margin-bottom: 0.75rem;
      }
    }
  }
`

const modularityContainerStyle = (withPartners: boolean) => css`
  ${withPartners && 'margin-bottom: 4rem'};
  @keyframes fadeInUp {
    0% {
      transform: translate3d(0, 100px, 0);
      opacity: 0;
    }
    100% {
      transform: translate3d(0, 0, 0);
      opacity: 1;
    }
  }

  .image-with-animation {
    animation-name: fadeInUp;
    animation-duration: 2s;
    animation-iteration-count: 1;
    animation-direction: normal;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
  }
`

const introImageStyle = css`
  padding: 0;

  @media screen and (min-width: 1250px) {
    padding: 2%;
  }

  @media screen and (min-width: 1350px) {
    padding: 3.5%;
  }
`
